import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { TASK_IDS } from "@/shared/constants/taskIds";
import { ClientStatusSelect } from "@/shared/components/ClientStatusSelect";
import { ClientStatusType } from "@/shared/types/clientStatus";
import { LawyerLayout } from "@/pages/layouts/LawyerLayout";
import { Button } from "@/shared/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Mail, Phone } from "lucide-react";
import { usePersonPhotoUrl } from "@/shared/hooks/usePersonPhotoUrl";
import { getInitials } from "@/shared/utils/formatters";
import { useEstateActivityLog } from "@/shared/hooks/useEstateActivityLog";
import { useLawyerActivitySeen } from "@/shared/hooks/useLawyerActivitySeen";
import { isUnreadForLawyer } from "@/shared/types/activityLog";
import { decryptPersonData } from "@/shared/utils/serverEncryption";
import { LAWYER_ROUTES } from "@/shared/constants/paths";
import CircularProgress from "@/shared/components/CircularProgress";
import DocumentSheet from "@/shared/components/document-viewer/DocumentSheet";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import { sentryUtils } from "@/shared/utils/sentry";
import { EstateItemSheet } from "@/shared/components/EstateItems/EstateItemSheet";
import { LawyerEstateInventoryTab } from "./tabs/EstateInventoryTab";
import OverviewTab from "./tabs/OverviewTab";
import DocumentsTab from "./tabs/DocumentsTab";
import { LawyerPeopleTab as PeopleTab } from "./tabs/PeopleTab";
import AssetSearchTab from "./tabs/AssetSearchTab";
import { ActivityLogTab } from "./tabs/ActivityLogTab";
import { LawyerEstateAccountTab } from "./tabs/EstateAccountTab";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import {
  findLinkedSecuredDebts,
  findLinkedProperty,
} from "@/pages/postloss/estate-inventory/utils/estateItemUtils";
import { useDemoMode } from "@/pages/demo/DemoModeContext";

const FAVORITE_DOCS_KEY = "lawyer-favorite-documents";
const LETTERS_DOC_TYPES = ["letters_testamentary", "letters_administration"];

const getFavoriteDocuments = (estateId: string): number[] => {
  try {
    const stored = localStorage.getItem(`${FAVORITE_DOCS_KEY}-${estateId}`);
    return stored ? JSON.parse(stored) : [];
  } catch (err) {
    sentryUtils.captureError(err, "Lawyer favorite docs parse failed", undefined, "warning");
    return [];
  }
};

const setFavoriteDocuments = (estateId: string, favorites: number[]): void => {
  localStorage.setItem(`${FAVORITE_DOCS_KEY}-${estateId}`, JSON.stringify(favorites));
};

const LawyerClientDetail = () => {
  const { userId: urlEstateId } = useParams<{ userId: string }>();
  const { isDemoMode, selectedDemoEstateId, setSelectedDemoEstateId } = useDemoMode();
  const [searchParams] = useSearchParams();
  const demoEstateParam = searchParams.get("estate");

  useEffect(() => {
    if (isDemoMode && demoEstateParam) {
      setSelectedDemoEstateId(Number(demoEstateParam));
    }
  }, [isDemoMode, demoEstateParam, setSelectedDemoEstateId]);

  const estateId = isDemoMode ? (demoEstateParam ?? String(selectedDemoEstateId)) : urlEstateId;
  const { photoUrl } = usePersonPhotoUrl(estateId ? Number(estateId) : null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navState = location.state as { tab?: string; openUpload?: boolean } | null;
  const [activeTab, setActiveTab] = useState(navState?.tab ?? "overview");

  const estateIdNum = estateId ? Number(estateId) : undefined;
  const { data: activityData } = useEstateActivityLog(estateIdNum);
  const { seenAt, markSeen } = useLawyerActivitySeen(estateIdNum);

  const hasUnreadActivity = useMemo(() => {
    const allEntries = activityData?.pages.flatMap((page) => page.entries) ?? [];
    return allEntries.some((entry) => isUnreadForLawyer(entry, seenAt));
  }, [activityData, seenAt]);

  useEffect(() => {
    if (activeTab === "activity-log" && hasUnreadActivity) {
      markSeen(new Date().toISOString());
    }
  }, [activeTab, hasUnreadActivity, markSeen]);
  const [selectedDocument, setSelectedDocument] = useState<{
    id: number;
    name: string;
    createdAt: string;
  } | null>(null);
  const [, setLoadingDocumentId] = useState<number | null>(null);
  const [showSSN, setShowSSN] = useState(false);
  const [favoriteDocIds, setFavoriteDocIds] = useState<number[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);

  useEffect(() => {
    if (estateId) {
      setFavoriteDocIds(getFavoriteDocuments(estateId));
      setShowSSN(false);
      setSelectedDocument(null);
      setSelectedItemId(null);
      setActiveTab(navState?.tab ?? "overview");
    }
  }, [estateId]);

  const toggleFavorite = (docId: number) => {
    if (!estateId) return;
    const newFavorites = favoriteDocIds.includes(docId)
      ? favoriteDocIds.filter((id) => id !== docId)
      : [...favoriteDocIds, docId];
    setFavoriteDocIds(newFavorites);
    setFavoriteDocuments(estateId, newFavorites);
  };

  const isDocFavorited = (docId: number) => favoriteDocIds.includes(docId);

  // Fetch person details
  const { data: person, isLoading: personLoading } = useQuery({
    queryKey: ["lawyer-person-detail", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estates")
        .select("*")
        .eq("estate_id", estateId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!estateId,
  });

  // Update client status mutation
  const updateClientStatusMutation = useMutation({
    mutationFn: async (status: ClientStatusType) => {
      if (isDemoMode) {
        queryClient.setQueryData(["lawyer-person-detail", estateId], (prev: unknown) =>
          prev ? { ...(prev as object), lawyer_client_status: status } : prev,
        );
        return;
      }
      const { error } = await supabase
        .from("estates")
        .update({ lawyer_client_status: status })
        .eq("estate_id", estateId);

      if (error) throw error;
    },
    onSuccess: () => {
      if (isDemoMode) return;
      queryClient.invalidateQueries({
        queryKey: ["lawyer-person-detail", estateId],
      });
    },
  });

  // Fetch and decrypt SSN
  const { data: decryptedSSN, isLoading: isLoadingSSN } = useQuery({
    queryKey: ["lawyer-person-ssn", estateId, showSSN],
    queryFn: async () => {
      if (!showSSN || !person?.ssn_encrypted) return null;
      const decrypted = await decryptPersonData(Number(estateId), person.ssn_encrypted, "ssn");
      return decrypted;
    },
    enabled: showSSN && !!person?.ssn_encrypted,
    retry: false,
  });

  const { data: intakeClient } = useQuery({
    queryKey: ["lawyer-intake-client", estateId],
    queryFn: async () => {
      const { data } = await supabase
        .from("lawyer_client_invitations")
        .select("client_first_name, client_last_name, client_email")
        .eq("estate_id", Number(estateId))
        .order("created_at", { ascending: false })
        .maybeSingle();
      return data;
    },
    enabled: !!estateId && !person?.full_name,
  });

  // Fetch account holder using the get-account-owner edge function
  const { data: owner } = useQuery({
    queryKey: ["lawyer-person-account-holder", estateId],
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) return null;

      const { data, error } = await supabase.functions.invoke("account/owner", {
        body: { estate_id: estateId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error || data?.error || !data?.owner) return null;

      const ownerData = data.owner;
      const fullName =
        ownerData.full_name ||
        `${ownerData.first_name || ""} ${ownerData.last_name || ""}`.trim() ||
        "Unknown";

      return {
        full_name: fullName,
        email: ownerData.email as string | undefined,
        phone_number: (ownerData.phone_number as string | null) || null,
      };
    },
    enabled: !!estateId,
  });

  // Fetch documents
  const { data: documents } = useQuery({
    queryKey: ["lawyer-person-documents", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_documents")
        .select(
          `
          *,
          uploaded_by:users!uploaded_by_user_id(
            user_id,
            full_name,
            first_name,
            last_name,
            email
          )
        `,
        )
        .eq("estate_id", estateId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!estateId,
  });

  // Intake answers that tell us a document does not exist (vs. just not uploaded).
  // The family confirms in intake whether there is a will / trust.
  const { data: naDocumentTypes } = useQuery({
    queryKey: ["lawyer-person-intake-answers", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_tasks")
        .select("task_id, answers")
        .eq("estate_id", Number(estateId))
        .in("task_id", [TASK_IDS.WILL, TASK_IDS.TRUST]);

      if (error) throw error;

      const naTypes = new Set<string>();
      for (const task of data ?? []) {
        const answers = (task.answers as Record<string, unknown> | null) ?? {};
        if (task.task_id === TASK_IDS.WILL && answers["has-will"] === "no") naTypes.add("will");
        if (task.task_id === TASK_IDS.TRUST && answers["has-trust"] === "no") naTypes.add("trust");
      }
      return naTypes;
    },
    enabled: !!estateId,
  });

  const {
    assets: estateAssets,
    debts: estateDebts,
    updateItem,
    deleteItem,
  } = useEstateItems(estateId ? Number(estateId) : undefined);
  const estateItems = useMemo(() => [...estateAssets, ...estateDebts], [estateAssets, estateDebts]);
  const selectedItem = useMemo(
    () => (selectedItemId ? (estateItems.find((i) => i.id === selectedItemId) ?? null) : null),
    [selectedItemId, estateItems],
  );
  const selectedLinkedDebts = useMemo(
    () => (selectedItem ? findLinkedSecuredDebts(selectedItem, estateItems) : []),
    [selectedItem, estateItems],
  );
  const selectedLinkedProperty = useMemo(
    () => (selectedItem ? findLinkedProperty(selectedItem, estateItems) : null),
    [selectedItem, estateItems],
  );

  // Fetch contacts
  const { data: contacts } = useQuery({
    queryKey: ["lawyer-person-contacts", estateId ? Number(estateId) : undefined],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_contacts")
        .select(
          `
          *,
          source_document:estate_documents!source_document_id(
            id,
            file_name,
            display_name,
            document_type,
            created_at
          )
        `,
        )
        .eq("estate_id", estateId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!estateId,
  });

  // Fetch invitations to determine invitation status
  const { data: invitations } = useQuery({
    queryKey: ["lawyer-person-invitations", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_invitations")
        .select("*")
        .eq("estate_id", estateId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!estateId,
  });

  // Fetch users who have joined (via user_estates table)
  const { data: joinedUsers } = useQuery({
    queryKey: ["lawyer-user-persons", estateId],
    queryFn: async () => {
      const { data, error: queryError } = await supabase
        .from("user_estates")
        .select("user_id, user:users(user_id, email)")
        .eq("estate_id", estateId);

      if (queryError) throw queryError;
      return (data ?? [])
        .map((row) => {
          const joined = Array.isArray(row.user) ? row.user[0] : row.user;
          return joined ? { user_id: joined.user_id, email: joined.email } : null;
        })
        .filter((u): u is { user_id: number; email: string | null } => u !== null);
    },
    enabled: !!estateId,
  });

  const hasLetters = useMemo(
    () => (documents ?? []).some((d) => LETTERS_DOC_TYPES.includes(d.document_type ?? "")),
    [documents],
  );
  const hasEin = useMemo(
    () => (documents ?? []).some((d) => d.document_type === "ein_confirmation"),
    [documents],
  );
  const decedentFirstName = person?.full_name?.split(" ")[0] ?? undefined;

  const ownerLegalRole = useMemo(() => {
    if (!owner?.email || !contacts) return [];
    const ownerEmail = owner.email.toLowerCase();
    const roles = new Set<string>();
    contacts.forEach((c) => {
      if (c.email?.toLowerCase() === ownerEmail && c.legal_role) {
        c.legal_role.forEach((role: string) => roles.add(role));
      }
    });
    return Array.from(roles);
  }, [owner, contacts]);

  // Sort documents with favorites first
  const sortedDocuments = useMemo(() => {
    if (!documents) return [];
    return [...documents].sort((a, b) => {
      const aFav = favoriteDocIds.includes(a.id);
      const bFav = favoriteDocIds.includes(b.id);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [documents, favoriteDocIds]);

  // Key documents: favorites first, then will, trust, death certificate,
  // and letters testamentary / of administration. Cap at 4.
  const formatPhoneNumber = (phone: string) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const handleDocumentClick = (doc: {
    id: number;
    name?: string;
    file_name: string | null;
    created_at: string;
  }) => {
    setLoadingDocumentId(doc.id);
    setSelectedDocument({
      id: doc.id,
      name: doc.name || doc.file_name || "Document",
      createdAt: doc.created_at,
    });
  };

  if (personLoading) {
    return (
      <LawyerLayout>
        <div className="flex items-center justify-center py-12">
          <CircularProgress />
        </div>
      </LawyerLayout>
    );
  }

  if (!person) {
    return (
      <LawyerLayout>
        <div className="text-center py-12">
          <p className="text-secondary">Estate not found or you don't have access.</p>
          <Button
            variant="outline"
            onClick={() => navigate(LAWYER_ROUTES.DASHBOARD)}
            className="mt-4"
          >
            Back to Dashboard
          </Button>
        </div>
      </LawyerLayout>
    );
  }

  return (
    <LawyerLayout>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full overflow-hidden flex-shrink-0 bg-brand text-sm font-medium text-white">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={`${person.full_name || "Client"} photo`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>
                  {getInitials({
                    fullName:
                      person.full_name ||
                      [intakeClient?.client_first_name, intakeClient?.client_last_name]
                        .filter(Boolean)
                        .join(" ") ||
                      "?",
                  })}
                </span>
              )}
            </div>
            <h1 className="text-2xl text-primary">
              {person.full_name
                ? `${person.full_name} Estate`
                : intakeClient
                  ? [intakeClient.client_first_name, intakeClient.client_last_name]
                      .filter(Boolean)
                      .join(" ")
                  : "Client"}
            </h1>
          </div>
          <ClientStatusSelect
            value={person.lawyer_client_status as ClientStatusType}
            onValueChange={(status) => updateClientStatusMutation.mutate(status)}
            disabled={updateClientStatusMutation.isPending}
          />
        </div>
        {owner && (
          <div className="mt-1">
            <p className="text-sm">
              <span className="text-primary">Primary Contact:</span>{" "}
              <span className="text-primary">{owner.full_name}</span>
            </p>
            <div className="flex items-center gap-4 mt-1">
              {owner.email && (
                <a
                  href={`mailto:${owner.email}`}
                  className="text-sm text-brand flex items-center gap-1.5 no-underline hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  {owner.email}
                </a>
              )}
              {owner.phone_number && (
                <a
                  href={`tel:${owner.phone_number}`}
                  className="text-sm text-brand flex items-center gap-1.5 no-underline hover:underline whitespace-nowrap"
                >
                  <Phone className="h-4 w-4" />
                  {formatPhoneNumber(owner.phone_number)}
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="border-b border-stone-200">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <TabsList hideMobileDropdown={true} className="border-b-0">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="estate-finances">Estate Inventory</TabsTrigger>
              <TabsTrigger value="estate-account">Estate Account</TabsTrigger>
              <TabsTrigger value="asset-search">Asset Search</TabsTrigger>
              <TabsTrigger value="people">People</TabsTrigger>
              <TabsTrigger value="activity-log">
                Activity Log
                {hasUnreadActivity && (
                  <span
                    className="ml-1.5 h-2 w-2 rounded-full bg-brand flex-shrink-0"
                    aria-label="Unread activity"
                  />
                )}
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab
            person={person}
            owner={owner}
            ownerLegalRole={ownerLegalRole}
            documents={documents || []}
            naDocumentTypes={naDocumentTypes}
            estateItems={estateItems || []}
            showSSN={showSSN}
            setShowSSN={setShowSSN}
            isLoadingSSN={isLoadingSSN}
            decryptedSSN={decryptedSSN}
            onDocumentClick={handleDocumentClick}
            onViewAllDocuments={() => setActiveTab("documents")}
            onViewAllEstateFinances={() => setActiveTab("estate-finances")}
            onViewAssetSearch={() => setActiveTab("asset-search")}
            onViewEstateAccount={() => setActiveTab("estate-account")}
          />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <DocumentsTab
            documents={documents || []}
            sortedDocuments={sortedDocuments}
            isDocFavorited={isDocFavorited}
            toggleFavorite={toggleFavorite}
            onDocumentClick={handleDocumentClick}
            estateId={estateId ? Number(estateId) : undefined}
            initialUploadOpen={navState?.openUpload === true && activeTab === "documents"}
          />
        </TabsContent>

        <TabsContent value="estate-finances" className="mt-6">
          <LawyerEstateInventoryTab
            estateId={Number(estateId)}
            onOpenDocument={(doc) =>
              handleDocumentClick({
                id: doc.id,
                name: doc.display_name || doc.file_name,
                file_name: doc.file_name,
                created_at: doc.created_at,
              })
            }
            onSelectItem={(item) => setSelectedItemId(item.id)}
          />
        </TabsContent>

        <TabsContent value="estate-account" className="mt-6">
          <LawyerEstateAccountTab
            estateId={Number(estateId)}
            decedentFirstName={decedentFirstName}
            decedentFullName={person?.full_name ?? undefined}
            hasLetters={hasLetters}
            hasEin={hasEin}
          />
        </TabsContent>

        <TabsContent value="asset-search" className="mt-6">
          <AssetSearchTab />
        </TabsContent>

        <TabsContent value="people" className="mt-6">
          <PeopleTab
            contacts={contacts}
            invitations={invitations}
            joinedUsers={joinedUsers}
            ownerEmail={owner?.email}
            estateId={estateId ? Number(estateId) : undefined}
            decedentName={person.full_name}
          />
        </TabsContent>

        <TabsContent value="activity-log" className="mt-6">
          <ActivityLogTab
            estateId={estateId ? Number(estateId) : undefined}
            seenAt={seenAt}
            onDocumentClick={handleDocumentClick}
          />
        </TabsContent>
      </Tabs>

      <EstateItemSheet
        item={selectedItem}
        estateId={estateId ? Number(estateId) : undefined}
        onClose={() => setSelectedItemId(null)}
        contacts={contacts ?? []}
        linkedSecuredDebts={selectedLinkedDebts}
        linkedProperty={selectedLinkedProperty}
        onSelectItem={setSelectedItemId}
        onUpdateItem={async (id, updates) => {
          await updateItem(id, updates);
        }}
        onDeleteItem={async (id) => {
          await deleteItem(id);
        }}
        onDocumentClick={(doc) => {
          setSelectedItemId(null);
          handleDocumentClick({
            id: doc.id,
            name: doc.name,
            file_name: doc.name,
            created_at: doc.createdAt,
          });
        }}
      />

      {/* Document Preview Sheet */}
      {selectedDocument && (
        <TooltipProvider>
          <DocumentSheet
            isOpen={true}
            setIsOpen={() => {
              setSelectedDocument(null);
              setLoadingDocumentId(null);
            }}
            documentId={selectedDocument.id}
            documentName={selectedDocument.name}
            documentCreatedAt={new Date(selectedDocument.createdAt)}
            estateId={Number(estateId)}
          />
        </TooltipProvider>
      )}
    </LawyerLayout>
  );
};

export default LawyerClientDetail;
